import "./style.scss";

import Close from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Box,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from "@mui/material";
import { Info } from "assets/img/svg/info";
import Pen from "assets/img/svg/pen";
import Checkmark from "assets/img/svg/validCheck";
import variables from "assets/styles/_variables.module.scss";
import EmptyState from "components/shared/EmptyState";
import { Spinner } from "components/shared/Spinner";
import ManagementTableTitleComponent from "components/shared/ManagementTableTitleComponent";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import useBindingKeyboard from "hooks/useBindingKeyboard";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { putStock } from "store/stockStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { PeriodStatus, TStock } from "store/types";
import type { TSortOrder } from "types";

import Pagination from "../shared/Pagination";

interface ILocation {
    name: any;
    titleId: number;
    inventory: number;
    locationId: number;
    pressInventory: number;
    scrappedInventory: number;
}

interface IStockPage {
    page: number;
    count: number;
    limitPagination: number;
    handleChangePage: (event: unknown, newPage: number) => void;
    handleChangeLimit: (event: React.ChangeEvent<HTMLInputElement>) => void;
    control: "all" | "invalid" | "valid";
    onControlChange: React.Dispatch<React.SetStateAction<string>>;
    handleSort: (name: string) => void;
    sortBy: string;
    sortOrder: TSortOrder;
    /**
     * Is period closed (0) or open.
     */
    periodStatus: PeriodStatus | undefined;
    /**
     * Handle search query
     */
    handleQuery?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    isRoyaltiesRecalculating?: boolean;
}

const StockPage = ({
    page,
    count,
    limitPagination,
    handleChangeLimit,
    handleChangePage,
    control,
    onControlChange,
    handleSort,
    sortBy,
    sortOrder,
    periodStatus,
    handleQuery,
    isRoyaltiesRecalculating = false,
}: IStockPage) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const nav = useNavigate();

    const { stocks, stockStatus, locations } = useAppSelector((state) => state.stock);
    const activePeriod = useAppSelector((state) => state.period.activePeriod);
    const isLoading = stockStatus === "pending" || isRoyaltiesRecalculating;

    const [editedTitle, setEditedTitle] = useState<null | TStock>(null);
    const [editMode, setEditMode] = useState(false);

    const isPeriodClosed = periodStatus === 0;

    const filteredStocks = useMemo(() => {
        if (control === "invalid") {
            return stocks.filter((stock) => stock.control !== 0);
        } else if (control === "valid") {
            return stocks.filter((stock) => stock.control === 0);
        } else {
            return stocks;
        }
    }, [stocks, control]);

    // Used for Collapse component
    const [showLocationsDetail, setShowLocationsDetail] = useState(false);
    const [currentCollapse, setCurrentCollapse] = useState<null | number>(null);

    /**
     * Function that opens or closes a row (open only one at a time)
     * @param boolean Open or not
     * @param id The id of the title selected
     */
    const handleOpenCollapse = (open: boolean, id: number | null) => {
        if (open) {
            setCurrentCollapse(id);
            setShowLocationsDetail(true);
        } else {
            // if one line is already collapsed, I'll be sending "false"
            if (currentCollapse !== id) {
                // because the currentCollapse is the previous line, I switch to the new line
                setCurrentCollapse(id);
                setShowLocationsDetail(true);
            } else {
                // simply close the line I just opened
                setCurrentCollapse(null);
                setShowLocationsDetail(false);
            }
        }
    };

    /**
     * Function that creates string for the tooltip for theoretical sales
     * @param title The title and its stock values
     */
    const getTooltipTheoretical = (title: TStock) => {
        return (
            <p style={{ fontSize: "0.75rem", lineHeight: "20px" }}>
                {t("Stock.TheoreticalSales")} = {title.initialInventory}{" "}
                <span style={{ color: "#BFBFBF" }}>({t("Stock.PreviousStock")})</span> + {title.printedInventory}{" "}
                <span style={{ color: "#BFBFBF" }}>({t("Stock.CurrentPrintRun")})</span> -{" "}
                {getTotalValues(title, "inventory")} <span style={{ color: "#BFBFBF" }}>({t("Stock.Stock")})</span> -{" "}
                {getTotalValues(title, "scrappedInventory")}{" "}
                <span style={{ color: "#BFBFBF" }}>({t("Stock.ScrappedInventory")})</span> -{" "}
                {getTotalValues(title, "pressInventory")}{" "}
                <span style={{ color: "#BFBFBF" }}>({t("Stock.PressInventory")})</span>
            </p>
        );
    };

    /**
     * Function that creates string for the tooltip for real sales
     * @param title The title and its stock values
     */
    const getTooltipReal = (title: TStock) => {
        return (
            <p className="tooltip-text">
                {t("Stock.RealSales")} = {title.grossQuantities}{" "}
                <span style={{ color: "#BFBFBF" }}>({t("Stock.GrossSales")})</span> - {title.returnsQuantities}{" "}
                <span style={{ color: "#BFBFBF" }}>({t("Stock.Returns")})</span>
            </p>
        );
    };

    /**
     * Function that gets total values for each column of the Locations section
     * @param title The title and its stock values
     * @param type The column concerned
     */
    const getTotalValues = (title: TStock | null, type: string) => {
        const total =
            title &&
            title.locations.reduce((prev, curr) => {
                return prev + +curr[type as keyof typeof curr];
            }, 0);
        return total || 0;
    };

    const getTheoreticalSales = (title: TStock) => {
        return (
            title &&
            title?.initialInventory +
                +title?.printedInventory -
                getTotalValues(title, "inventory") -
                getTotalValues(title, "scrappedInventory") -
                getTotalValues(title, "pressInventory")
        );
    };

    /**
     * Selects title that is being edited and sets edit mode (change opacity of other lines)
     * @param title The title and its stock values
     */
    const handleTitleStockEdit = (title: TStock) => {
        const formattedTitle = structuredClone(title);
        formattedTitle.printedInventory =
            formattedTitle.printedInventory > 0 ? formattedTitle.printedInventory : ("" as any);
        formattedTitle.locations = formattedTitle.locations.map((loc) => ({
            ...loc,
            inventory: loc.inventory > 0 ? loc.inventory : ("" as any),
            scrappedInventory: loc.scrappedInventory > 0 ? loc.scrappedInventory : ("" as any),
            pressInventory: loc.pressInventory > 0 ? loc.pressInventory : ("" as any),
        }));
        setEditedTitle(editedTitle?.id ? editedTitle : formattedTitle);
        setEditMode(true);
        handleOpenCollapse(true, title.id);
    };

    /**
     * Function that takes new values written in the different inputs and saves a new TStock state with updated values ready for saving
     * @param value Value written in the input
     * @param type Which input this value comes from
     * @param location What location (emplacement) it belongs to (if the input is for a location)
     */
    const handleValueChange = (value: string, type: string, location?: ILocation) => {
        // Handle empty field (never have it empty or it saves NaN)
        // const newValue = !value || value === "" ? "0" : value;
        const newValue = value;

        if (/^[0-9]*$/.test(newValue) === false) return;

        // At some point I needed this line to make sure you got from 0 to a number and not 01 or 02, but now it seems it works without ?
        // newValue = newValue.charAt(0) === "0" && newValue.length > 1 ? newValue.replace("0", "") : newValue

        const newLocation = { ...location, [type]: newValue };

        const newLocations = editedTitle?.locations.map((loc) => {
            if (loc.locationId === location?.locationId) {
                return newLocation;
            } else {
                return loc;
            }
        });

        const newTitleStock =
            type === "printedInventory"
                ? { ...editedTitle, printedInventory: newValue }
                : { ...editedTitle, locations: newLocations };

        setEditedTitle(newTitleStock as TStock);
    };

    const handleSave = (title: TStock) => {
        dispatch(
            putStock({
                titleId: title.id,
                data: {
                    printedInventory: +title.printedInventory,
                    locations: title.locations.map((loc) => ({
                        ...loc,
                        inventory: +loc.inventory,
                        scrappedInventory: +loc.scrappedInventory,
                        pressInventory: +loc.pressInventory,
                    })),
                },
                activePeriod: activePeriod || -1,
            }),
        ).then(() => {
            setEditMode(false);
            setEditedTitle(null);
        });
    };

    // For adjusting size of inner table to headings
    const locationWidth = (document?.querySelector("#locations") as any)?.offsetWidth;
    const stockWidth = (document?.querySelector("#stock") as any)?.offsetWidth;
    const pressWidth = (document?.querySelector("#pressInventory") as any)?.offsetWidth;
    const scrappedWidth = (document?.querySelector("#scrappedInventory") as any)?.offsetWidth;

    const [firstLoad, setFirstLoad] = useState(true);

    useEffect(() => {
        // Used to open first row on initial render
        if (stocks.length > 0 && firstLoad) {
            handleOpenCollapse(true, stocks[0].id);
            setFirstLoad(false);
        }
    }, [stocks]);

    const handleEscapeEditMode = (title: TStock | null) => {
        handleOpenCollapse(false, title && title?.id);
        setEditedTitle(null);
        setEditMode(false);
    };
    useBindingKeyboard({
        action: () => handleEscapeEditMode(editedTitle),
        type: "keydown",
        touchToBind: ["Escape"],
    });

    return (
        <div className="stock-table">
            {!locations.length && !isLoading ? (
                <div className="stock-empty-state">
                    <EmptyState type={"stockNoLocations"} />
                </div>
            ) : (
                <>
                    {isPeriodClosed && (
                        <MessageBlock
                            type="info"
                            text={t("Stock.StockClosedInfo")}
                            small
                            style={{ marginBottom: "10px" }}
                            link="https://helpdesk.crealo.app/fr/article/cloturer-une-periode-xhf5s5/"
                        />
                    )}
                    <Paper
                        sx={{
                            width: "100%",
                            border: `1px solid ${variables["border-grey"]}`,
                            borderRadius: "4px",
                            boxShadow: "none",
                        }}
                    >
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="table-head">
                                            <TableSortLabel
                                                active={sortBy === "name"}
                                                direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                                onClick={() => handleSort("name")}
                                            >
                                                {t("Stock.Title")}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-head center" style={{ minWidth: "6.25rem" }}>
                                            {t("Stock.PreviousStock")}
                                        </TableCell>
                                        <TableCell className="table-head center" style={{ minWidth: "6.87rem" }}>
                                            {t("Stock.CurrentPrintRun")}
                                        </TableCell>
                                        <TableCell id="locations" className="table-head left-border center">
                                            {t("Stock.Location")}
                                        </TableCell>
                                        <TableCell id="stock" className="table-head center locations-details">
                                            {t("Stock.Stock")}
                                        </TableCell>
                                        <TableCell
                                            id="scrappedInventory"
                                            className="table-head center locations-details"
                                        >
                                            {t("Stock.ScrappedInventory")}
                                        </TableCell>
                                        <TableCell id="pressInventory" className="table-head center locations-details">
                                            {t("Stock.PressInventory")}
                                        </TableCell>
                                        <TableCell className="table-head"></TableCell>
                                        <TableCell
                                            className="table-head center left-border"
                                            style={{ paddingInline: "10px" }}
                                        >
                                            {t("Stock.SalesCheck")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading && (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                <Spinner size={36} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoading && filteredStocks.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        width: "100%",
                                                        paddingBlock: "30px",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <EmptyState
                                                        type={
                                                            control === "all"
                                                                ? "stockBoard"
                                                                : control === "valid"
                                                                  ? "stockValid"
                                                                  : "stockInvalid"
                                                        }
                                                        onControlChange={(value: string) => onControlChange(value)}
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoading &&
                                        filteredStocks.map((title) => {
                                            const isValid = title.control === 0;
                                            return (
                                                <>
                                                    <TableRow
                                                        className={`${
                                                            editMode && editedTitle?.id === title.id && "border-top"
                                                        } ${isPeriodClosed ? "closed-period" : ""} table-row`}
                                                        style={
                                                            editMode && editedTitle?.id !== title.id
                                                                ? { opacity: 0.1, pointerEvents: "none" }
                                                                : {}
                                                        }
                                                        key={title.id}
                                                    >
                                                        {/* Title of the book and ISBN */}
                                                        <TableCell>
                                                            <ManagementTableTitleComponent
                                                                nav={() => nav(`/titles/${title.id}`)}
                                                                sx={{ marginLeft: "0" }}
                                                                isbn={title.isbn}
                                                                title={title.name}
                                                                titleCoverUrl={title.avatarUrl}
                                                                handleQuery={handleQuery}
                                                            />
                                                        </TableCell>

                                                        {/* Stock N-1 */}
                                                        <TableCell className="cell-center">
                                                            {title.initialInventory}
                                                        </TableCell>

                                                        {/* Books printed during period */}
                                                        <TableCell className="cell-center">
                                                            <div className="print">
                                                                {editedTitle?.id === title.id ? (
                                                                    <StyledTextField
                                                                        className="edit-field"
                                                                        value={editedTitle?.printedInventory}
                                                                        placeholder="0"
                                                                        onChange={(e: any) =>
                                                                            handleValueChange(
                                                                                e.target.value,
                                                                                "printedInventory",
                                                                            )
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        <span>{title.printedInventory}</span>
                                                                        {!isPeriodClosed && (
                                                                            <button
                                                                                className="edit-btn"
                                                                                onClick={() =>
                                                                                    handleTitleStockEdit(title)
                                                                                }
                                                                            >
                                                                                <Pen />
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </TableCell>

                                                        {/* Locations and totals + details (collapsable) */}
                                                        <TableCell className="cell-center left-border" colSpan={4}>
                                                            <TableRow className="collapsable-row">
                                                                <TableCell
                                                                    className="collapsable-cell names center"
                                                                    style={{ width: `${locationWidth}px` }}
                                                                    onClick={() => {
                                                                        handleOpenCollapse(
                                                                            !showLocationsDetail,
                                                                            title.id,
                                                                        );
                                                                    }}
                                                                >
                                                                    {showLocationsDetail &&
                                                                    currentCollapse === title.id ? (
                                                                        <ExpandLessIcon sx={{ width: "50px" }} />
                                                                    ) : (
                                                                        <ExpandMoreIcon sx={{ width: "50px" }} />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    className="collapsable-cell center"
                                                                    style={{ width: `${stockWidth}px` }}
                                                                >
                                                                    <span>
                                                                        {editedTitle?.id === title.id
                                                                            ? getTotalValues(editedTitle, "inventory")
                                                                            : getTotalValues(title, "inventory")}
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell
                                                                    className="collapsable-cell center"
                                                                    style={{ width: `${scrappedWidth}px` }}
                                                                >
                                                                    <span>
                                                                        {editedTitle?.id === title.id
                                                                            ? getTotalValues(
                                                                                  editedTitle,
                                                                                  "scrappedInventory",
                                                                              )
                                                                            : getTotalValues(
                                                                                  title,
                                                                                  "scrappedInventory",
                                                                              )}
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell
                                                                    className="collapsable-cell center"
                                                                    style={{ width: `${pressWidth}px` }}
                                                                >
                                                                    <span>
                                                                        {editedTitle?.id === title.id
                                                                            ? getTotalValues(
                                                                                  editedTitle,
                                                                                  "pressInventory",
                                                                              )
                                                                            : getTotalValues(title, "pressInventory")}
                                                                    </span>
                                                                </TableCell>
                                                            </TableRow>
                                                            <Collapse
                                                                in={
                                                                    (showLocationsDetail || editMode) &&
                                                                    currentCollapse === title.id
                                                                }
                                                                timeout="auto"
                                                                unmountOnExit
                                                            >
                                                                {title.locations.length > 0 &&
                                                                    title.locations.map((location, i) => {
                                                                        const currentLoc = editedTitle?.locations.find(
                                                                            (loc) =>
                                                                                loc.locationId === location.locationId,
                                                                        );
                                                                        return (
                                                                            <TableRow
                                                                                className="collapsable-row"
                                                                                key={i}
                                                                            >
                                                                                <TableCell
                                                                                    className="collapsable-cell names"
                                                                                    style={{
                                                                                        width: `${locationWidth}px`,
                                                                                    }}
                                                                                >
                                                                                    <p
                                                                                        className={`${
                                                                                            editMode &&
                                                                                            editedTitle?.id ===
                                                                                                title.id &&
                                                                                            "margin-top"
                                                                                        } locations-values`}
                                                                                    >
                                                                                        {location.name}
                                                                                    </p>
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    className="collapsable-cell"
                                                                                    style={{ width: `${stockWidth}px` }}
                                                                                >
                                                                                    {(showLocationsDetail ||
                                                                                        editMode) &&
                                                                                    editedTitle?.id === title.id ? (
                                                                                        <div className="locations-edit">
                                                                                            <StyledTextField
                                                                                                className="edit-field"
                                                                                                value={
                                                                                                    currentLoc?.inventory
                                                                                                }
                                                                                                placeholder="0"
                                                                                                onChange={(e: any) =>
                                                                                                    handleValueChange(
                                                                                                        e.target.value,
                                                                                                        "inventory",
                                                                                                        currentLoc,
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <p className="locations-values">
                                                                                            {location.inventory}
                                                                                        </p>
                                                                                    )}
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    className="collapsable-cell"
                                                                                    style={{
                                                                                        width: `${scrappedWidth}px`,
                                                                                    }}
                                                                                >
                                                                                    {(showLocationsDetail ||
                                                                                        editMode) &&
                                                                                    editedTitle?.id === title.id ? (
                                                                                        <div className="locations-edit">
                                                                                            <StyledTextField
                                                                                                className="edit-field"
                                                                                                value={
                                                                                                    currentLoc?.scrappedInventory
                                                                                                }
                                                                                                placeholder="0"
                                                                                                onChange={(e: any) =>
                                                                                                    handleValueChange(
                                                                                                        e.target.value,
                                                                                                        "scrappedInventory",
                                                                                                        currentLoc,
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <p className="locations-values">
                                                                                            {location.scrappedInventory}
                                                                                        </p>
                                                                                    )}
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    className="collapsable-cell"
                                                                                    style={{ width: `${pressWidth}px` }}
                                                                                >
                                                                                    {(showLocationsDetail ||
                                                                                        editMode) &&
                                                                                    editedTitle?.id === title.id ? (
                                                                                        <div className="locations-edit">
                                                                                            <StyledTextField
                                                                                                className="edit-field"
                                                                                                value={
                                                                                                    currentLoc?.pressInventory
                                                                                                }
                                                                                                placeholder="0"
                                                                                                onChange={(e: any) =>
                                                                                                    handleValueChange(
                                                                                                        e.target.value,
                                                                                                        "pressInventory",
                                                                                                        currentLoc,
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <p className="locations-values">
                                                                                            {location.pressInventory}
                                                                                        </p>
                                                                                    )}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                            </Collapse>
                                                        </TableCell>

                                                        {/* Button to edit values in locations */}
                                                        <TableCell
                                                            className="center"
                                                            style={{ paddingInline: "10px", width: "50px" }}
                                                        >
                                                            {!isPeriodClosed && (
                                                                <button
                                                                    className="edit-btn"
                                                                    onClick={() => handleTitleStockEdit(title)}
                                                                    hidden={editMode && editedTitle?.id === title.id}
                                                                >
                                                                    <Pen />
                                                                </button>
                                                            )}
                                                        </TableCell>

                                                        {/* Checks : if theoretical sales = real sales -> yes, then green checkmark; no, cell in red */}
                                                        <TableCell
                                                            className={` ${
                                                                (
                                                                    editMode && editedTitle?.id === title.id
                                                                        ? editedTitle.control
                                                                        : isValid
                                                                )
                                                                    ? "grey-background"
                                                                    : "red-background"
                                                            } left-border`}
                                                        >
                                                            <div className="control-container">
                                                                <TableCell className="control-cell-section">
                                                                    <Tooltip
                                                                        title={
                                                                            editedTitle?.id === title.id
                                                                                ? getTooltipTheoretical(editedTitle)
                                                                                : getTooltipTheoretical(title)
                                                                        }
                                                                        componentsProps={{
                                                                            tooltip: {
                                                                                sx: {
                                                                                    bgcolor: "common.black",
                                                                                    "& .MuiTooltip-arrow": {
                                                                                        color: "common.black",
                                                                                    },
                                                                                },
                                                                            },
                                                                        }}
                                                                    >
                                                                        <div className="control-cell-values">
                                                                            <span className="number">
                                                                                {editedTitle?.id === title.id
                                                                                    ? getTheoreticalSales(editedTitle)
                                                                                    : title.theoricSales ?? 0}
                                                                            </span>
                                                                            <span className="text">
                                                                                {t("Stock.Theoretical")}
                                                                            </span>
                                                                        </div>
                                                                    </Tooltip>
                                                                </TableCell>
                                                                <TableCell className="control-cell-section">
                                                                    <span>-</span>
                                                                </TableCell>
                                                                <TableCell className="control-cell-section">
                                                                    <Tooltip
                                                                        title={getTooltipReal(title)}
                                                                        componentsProps={{
                                                                            tooltip: {
                                                                                sx: {
                                                                                    bgcolor: "common.black",
                                                                                    "& .MuiTooltip-arrow": {
                                                                                        color: "common.black",
                                                                                    },
                                                                                },
                                                                            },
                                                                        }}
                                                                    >
                                                                        <div className="control-cell-values">
                                                                            <span className="number">
                                                                                {title.netQuantities}
                                                                            </span>
                                                                            <span className="text">
                                                                                {t("Stock.Real")}
                                                                            </span>
                                                                        </div>
                                                                    </Tooltip>
                                                                </TableCell>
                                                                <TableCell className="control-cell-section">
                                                                    <span>=</span>
                                                                </TableCell>
                                                                <TableCell className="control-cell-section">
                                                                    <div className="control-cell-check">
                                                                        <span
                                                                            className={`number ${
                                                                                isValid ? "green" : "red"
                                                                            }`}
                                                                        >
                                                                            {editedTitle?.id === title.id
                                                                                ? getTheoreticalSales(editedTitle) -
                                                                                  title.netQuantities
                                                                                : title.control}
                                                                        </span>
                                                                        {isValid ? (
                                                                            <Checkmark />
                                                                        ) : (
                                                                            <Close
                                                                                sx={{
                                                                                    width: "15px",
                                                                                    height: "15px",
                                                                                    fill: "#9B5067",
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                    {editedTitle?.id === title.id && (
                                                        <div className="edit-action-btns">
                                                            <StyledButton
                                                                buttonType="outlined"
                                                                onClick={() => {
                                                                    handleEscapeEditMode(title);
                                                                }}
                                                            >
                                                                <Close sx={{ width: "15px", height: "15px" }} />{" "}
                                                                {t("Stock.Cancel")}
                                                            </StyledButton>
                                                            <StyledButton
                                                                buttonType="primary"
                                                                onClick={() => handleSave(editedTitle)}
                                                            >
                                                                <Checkmark stroke="#FEFEFE" /> {t("Stock.Save")}
                                                            </StyledButton>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component={() => (
                                <Pagination
                                    rowsPerPageOptions={[10, 25, 100]}
                                    rowsPerPage={limitPagination}
                                    currentPageSize={stocks.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeLimit}
                                    type="titles"
                                    count={count}
                                    sticky={true}
                                />
                            )}
                            count={-1}
                            rowsPerPage={limitPagination}
                            page={page}
                            onPageChange={handleChangePage}
                        />
                    </Paper>
                </>
            )}
        </div>
    );
};

export default StockPage;
